'use client';

import { useCallback } from 'react';
import { User, OneShotUserAccountSubscriber, PublicKey } from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import { getOracleCrankIxs } from 'src/utils/oracle';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import captureException from 'src/utils/captureException';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useDriftClient from 'src/hooks/useDriftClient';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';
import { useHandleTxChain } from 'src/hooks/driftActions/transactions/useHandleTxChain';

/**
 * Hook for settling PnL for a specific market and user
 */
export const useSettlePnl = () => {
	const driftClient = useDriftClient();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();
	const { handleTxChain } = useHandleTxChain();
	const currentSlotRef = useCurrentSlotRef();

	const settlePnl = useCallback(
		async (marketIndex: number, userPubKey?: PublicKey): Promise<boolean> => {
			if (!driftClient || !walletAdapter) return false;
			const toastId = `settlepnl-${Date.now()}`;

			// if userPubKey is passed then settle that user, if not then settle the current connected user
			const pubKeyToSettle =
				userPubKey ?? (currentUserAccount?.pubKey as PublicKey);
			let userAccountToSettle = currentUserAccount?.client?.getUserAccount();
			if (!userAccountToSettle) return false;
			if (userPubKey) {
				const user = new User({
					driftClient: driftClient,
					userAccountPublicKey: pubKeyToSettle,
					accountSubscription: {
						type: 'custom',
						userAccountSubscriber: new OneShotUserAccountSubscriber(
							driftClient.program,
							pubKeyToSettle
						),
					},
				});
				await user.subscribe();
				userAccountToSettle = user.getUserAccount();
			}

			notify({
				type: 'info',
				message: 'Awaiting Transaction',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				const marketIds = [MarketId.createPerpMarket(marketIndex)];

				const optionalIxs = await getOracleCrankIxs(
					marketIds,
					driftClient,
					walletAdapter.name,
					{ current: currentSlotRef.current },
					undefined
				);

				const _tx = await handleTxChain(
					driftClient.settlePNL(
						pubKeyToSettle,
						userAccountToSettle,
						marketIndex,
						getTxParams({
							marketId: MarketId.createPerpMarket(marketIndex),
						}),
						optionalIxs
					)
				);

				notify({
					type: 'success',
					message: 'Settled P&L.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return true;
			} catch (e: any) {
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					fallbackDescription: `Sorry, there was an unknown error settling your PnL.`,
					toastId,
					wallet: walletAdapter,
				});

				return false;
			}
		},
		[driftClient, walletAdapter, getTxParams, currentUserAccount, handleTxChain]
	);

	return { settlePnl };
};
